import React, { useEffect, useState, useRef, useCallback } from "react";
import Draggable from "react-draggable";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import commonActions from "../../common/actions";
import Portal from "../../common/components/Portal";
import ConfirmationModal from "../../common/components/ConfirmationModal";
import ReactTooltip from "react-tooltip";

const BulkImageDownload = ({ masterFilters, imageExport, cancelForm, isLoading = false, totalCount }) => {
    const initialFormState = {
        is_remove_uuid: false
    };
    const dispatch = useDispatch();
    const [formData, setFormData] = useState(initialFormState);
    const [onCancelClick, setOnCancelClick] = useState(false);
    const initialDataRef = useRef(initialFormState);
    const [showExportConfirmationModal, setShowExportConfirmationModal] = useState(false);

    const { isFormDirty: isDirty } = useSelector(state => state.commonReducer);

    useEffect(() => {
        dispatch(commonActions.setFormDirty(!_.isEqual(formData, initialDataRef.current)));
    }, [formData]);

    useEffect(() => {
        return () => {
            dispatch(commonActions.setFormDirty(false));
        };
    }, []);

    const handleCancel = useCallback(() => {
        // if (isDirty) {
        //     setOnCancelClick(true);
        //     return;
        // }
        // setOnCancelClick(false);
        cancelForm();
    }, [isDirty, cancelForm]);

    useEffect(() => {
        const handleKeyDown = e => {
            if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                handleCancel();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleCancel]);

    const handleExport = () => {
        setShowExportConfirmationModal(false);
        imageExport(formData);
    };

    const renderConfirmationModal = () => {
        if (!onCancelClick) return null;
        return (
            <Portal
                body={
                    <ConfirmationModal
                        type="cancel"
                        heading={"You have unsaved changes!"}
                        message={"This action cannot be reverted, are you sure you want to leave?"}
                        onNo={() => setOnCancelClick(false)}
                        onYes={() => {
                            dispatch(commonActions.setFormDirty(false));
                            cancelForm();
                        }}
                    />
                }
                onCancel={() => setOnCancelClick(false)}
            />
        );
    };

    const renderExportConfirmationModal = () => {
        if (!showExportConfirmationModal) return null;
        const filterType = masterFilters?.building_ids?.length > 0 ? "building" : masterFilters?.site_ids?.length > 0 ? "site" : "";
        const totalImageCount =
            filterType === "building"
                ? (masterFilters?.building_ids ?? [])?.length
                : filterType === "site"
                  ? (masterFilters?.site_ids ?? [])?.length
                  : [];

        return (
            <Portal
                body={
                    <ConfirmationModal
                        type="cancel"
                        heading={
                            <div>
                                You are about to download{" "}
                                <span className="badge-red-circled pl-2 pr-2 m-0" style={{ minWidth: "20px", maxWidth: "100px", width: "auto" }}>
                                    {totalCount}
                                </span>{" "}
                                {totalCount === 1 ? "image" : "images"} from{" "}
                                <span className="badge-red-circled pl-2 pr-2 m-0" style={{ minWidth: "20px", maxWidth: "100px", width: "auto" }}>
                                    {totalImageCount}
                                </span>{" "}
                                {totalImageCount === 1 ? filterType : `${filterType}s`}.
                            </div>
                        }
                        message={
                            <>
                                The images will be prepared as{" "}
                                {totalImageCount === 1 ? (
                                    "a"
                                ) : (
                                    <span className="badge-red-circled pl-2 pr-2 m-0" style={{ minWidth: "20px", maxWidth: "100px", width: "auto" }}>
                                        {totalImageCount}
                                    </span>
                                )}{" "}
                                {totalImageCount === 1 ? "ZIP file" : "ZIP files"}. This may take a few minutes, depending on the number of images.
                                <br />
                                Once ready, the {totalImageCount === 1 ? "ZIP file" : "ZIP files"} will be available in the <b>Files tab</b> for
                                download.
                                <br />
                                <b>Do you want to continue?</b>
                            </>
                        }
                        onNo={() => setShowExportConfirmationModal(false)}
                        onYes={handleExport}
                        cancel={() => setShowExportConfirmationModal(false)}
                        messageClassName="pl-3 pr-3"
                    />
                }
                onCancel={() => setShowExportConfirmationModal(false)}
            />
        );
    };

    return (
        <React.Fragment>
            <ReactTooltip id="download-info" className="rc-tooltip-custom-class" effect="solid" html={true} place="top" backgroundColor="#007bff" />
            <ReactTooltip id="export-type" className="rc-tooltip-custom-class" effect="solid" html={true} place="bottom" backgroundColor="#007bff" />
            <div
                className="modal modal-region image-bulk-download add-image-modal img-pop-small"
                id="modalId"
                style={{ display: "block" }}
                tabIndex="-1"
                role="dialog"
                aria-labelledby="exampleModalLabel"
                aria-hidden="true"
            >
                <Draggable handle=".draggable" cancel=".not-draggable">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header draggable">
                                <h5 className="modal-title" id="exampleModalLabel">
                                    Download Images
                                </h5>
                                <button type="button" className="close not-draggable" data-dismiss="modal" aria-label="Close" onClick={handleCancel}>
                                    <span aria-hidden="true">
                                        <img src="/img/close.svg" alt="" />
                                    </span>
                                </button>
                            </div>
                            <div className="add-project">
                                <div className="modal-body region-otr image-bulk-download core-rpt-popup">
                                    <div className="form-group item content-area">
                                        <div className="col-md-12 formInp pl-0 radio-area">
                                            <label className="heading export-form-inp">
                                                <h3>
                                                    Remove UUID if exist in image name{" "}
                                                    <i
                                                        class="fa fa-info search-info-icon mr-3 global-search-info"
                                                        data-tip={`
                                                        Removes UUID prefix from the image file name.<br/>
                                                        Example:<br/> "7f388281-598b-40c9-96b5-9329b8d1699d__ADC_Survey_1.jpg"
                                                        <br/>will be renamed to "ADC_Survey_1.jpg"
                                                    `}
                                                        data-for="download-info"
                                                    />
                                                </h3>
                                            </label>
                                            <div className="button-group">
                                                <div className="button">
                                                    <label className="container cursor-hand">
                                                        <input
                                                            type="radio"
                                                            id="remove_uuid_yes"
                                                            name="is_remove_uuid"
                                                            checked={formData.is_remove_uuid === true}
                                                            onChange={() => setFormData({ ...formData, is_remove_uuid: true })}
                                                        />
                                                        <span className="checkmark"></span>
                                                        Yes
                                                    </label>
                                                </div>
                                                <div className="button">
                                                    <label className="container cursor-hand">
                                                        <input
                                                            type="radio"
                                                            id="remove_uuid_no"
                                                            name="is_remove_uuid"
                                                            checked={formData.is_remove_uuid === false}
                                                            onChange={() => setFormData({ ...formData, is_remove_uuid: false })}
                                                        />
                                                        <span className="checkmark"></span>
                                                        No
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="down-area btn-area mt-2 d-flex">
                                            <button type="button" className="btn btn-download outdated mb-1 mr-4" onClick={handleCancel}>
                                                Cancel
                                            </button>
                                            {isLoading ? (
                                                <button type="button" className="btn btn-download mb-3">
                                                    <div className="button-loader d-flex justify-content-center align-items-center">
                                                        <div className="spinner-border text-white" role="status">
                                                            <span className="sr-only">Loading...</span>
                                                        </div>
                                                    </div>
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="btn btn-download mb-3"
                                                    onClick={() => setShowExportConfirmationModal(true)}
                                                    disabled={isLoading}
                                                >
                                                    Export
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Draggable>
            </div>
            {/* {renderConfirmationModal()} */}
            {renderExportConfirmationModal()}
        </React.Fragment>
    );
};

export default BulkImageDownload;
